// =============================================================================
// Module Core - Repository: Member
// Accès DB pour les membres d'organisation
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { 
  OrganizationMember, 
  OrganizationMemberWithProfile, 
  Profile,
  MemberStatus 
} from '../types';

// ─────────────────────────────────────────────────────────────────────────────
// USER MEMBERSHIPS — used at sign-in to detect fully suspended/removed users
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchUserMembershipStatuses(
  userId: string,
): Promise<Array<{ status: string }>> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select('status')
    .eq('user_id', userId);
  if (error) {
    console.warn('[memberRepository] fetchUserMembershipStatuses failed', error);
    return [];
  }
  return (data ?? []) as Array<{ status: string }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// CHECK USER SUSPENSION (RPC) — used when a user has no active org
// ─────────────────────────────────────────────────────────────────────────────
export async function checkUserSuspension(): Promise<boolean> {
  const { data, error } = await supabase.rpc('check_user_suspension');
  if (error) {
    console.warn('[memberRepository] checkUserSuspension failed', error);
    return false;
  }
  return data === true;
}

// ─────────────────────────────────────────────────────────────────────────────
// USER ACTIVE MEMBERSHIPS — used during realtime suspension/removal events
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchUserActiveMemberships(
  userId: string,
): Promise<Array<{ id: string; org_id: string }>> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select('id, org_id')
    .eq('user_id', userId)
    .eq('status', 'active');
  if (error) {
    console.warn('[memberRepository] fetchUserActiveMemberships failed', error);
    return [];
  }
  return (data ?? []) as Array<{ id: string; org_id: string }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH ORGANIZATION MEMBERS
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchOrganizationMembers(
  orgId: string,
  status?: MemberStatus | MemberStatus[]
): Promise<OrganizationMemberWithProfile[]> {
  const statusFilter = status ?? 'active';
  const isArray = Array.isArray(statusFilter);
  console.log('[MemberRepo] Fetching members for org:', orgId, 'status:', statusFilter);

  // DIRECT QUERY: keep fields aligned with current schema (including manager_id)
  console.log('[MemberRepo] Using direct query...');
  let query = supabase
    .from('core_organization_members')
    .select(`
      id, org_id, user_id, status,
      weekly_capacity_hours, working_days, manager_id,
      created_at, updated_at, created_by, updated_by,
      core_profiles ( * )
    `)
    .eq('org_id', orgId);

  if (isArray) {
    query = query.in('status', statusFilter);
  } else {
    query = query.eq('status', statusFilter);
  }

  const { data, error } = await query;

  if (error) {
    console.error('[MemberRepo] Direct query failed:', error.message, error.code, error.details);
    throw new Error(`[MemberRepo] Toutes les méthodes ont échoué. RPC + Direct query. Erreur: ${error.message}`);
  }

  console.log('[MemberRepo] Direct query returned', data?.length, 'members');

  return (data || []).map((row) => ({
    id: row.id,
    org_id: row.org_id,
    user_id: row.user_id,
    status: row.status as MemberStatus,
    weekly_capacity_hours: (row as any).weekly_capacity_hours ?? null,
    working_days: (row as any).working_days ?? null,
    manager_id: (row as any).manager_id ?? null,
    created_at: row.created_at,
    updated_at: row.updated_at,
    created_by: row.created_by,
    updated_by: row.updated_by,
    profile: {
      id: (row.core_profiles as unknown as any)?.id || row.user_id,
      full_name: (row.core_profiles as unknown as any)?.full_name || null,
      email: (row.core_profiles as unknown as any)?.email || '',
      avatar_url: (row.core_profiles as unknown as any)?.avatar_url || null,
      job_title: (row.core_profiles as unknown as any)?.job_title || null,
    },
  }));
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH MEMBER BY USER ID
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchMemberByUserId(
  orgId: string,
  userId: string
): Promise<OrganizationMember | null> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select('*')
    .eq('org_id', orgId)
    .eq('user_id', userId)
    .eq('status', 'active')
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null; // Not found
    console.error('Error fetching member by user id:', error);
    return null;
  }

  return data as OrganizationMember;
}

// ─────────────────────────────────────────────────────────────────────────────
// FETCH MEMBER BY ID
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchMemberById(
  memberId: string
): Promise<OrganizationMemberWithProfile | null> {
  const { data, error } = await supabase
    .from('core_organization_members')
    .select(`
      id,
      org_id,
      user_id,
      status,
      weekly_capacity_hours,
      working_days,
      created_at,
      updated_at,
      created_by,
      updated_by,
      core_profiles ( * )
    `)
    .eq('id', memberId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    console.error('Error fetching member by id:', error);
    return null;
  }

  return {
    id: data.id,
    org_id: data.org_id,
    user_id: data.user_id,
    status: data.status as MemberStatus,
    weekly_capacity_hours: (data as any).weekly_capacity_hours ?? null,
    working_days: (data as any).working_days ?? null,
    manager_id: (data as any).manager_id ?? null,
    created_at: data.created_at,
    updated_at: data.updated_at,
    created_by: data.created_by,
    updated_by: data.updated_by,
    profile: {
      id: (data.core_profiles as unknown as any)?.id || data.user_id,
      full_name: (data.core_profiles as unknown as any)?.full_name || null,
      email: (data.core_profiles as unknown as any)?.email || '',
      avatar_url: (data.core_profiles as unknown as any)?.avatar_url || null,
      job_title: (data.core_profiles as unknown as any)?.job_title || null,
    },
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE MEMBER STATUS
// ─────────────────────────────────────────────────────────────────────────────
export async function updateMemberStatus(
  orgId: string,
  memberId: string,
  status: MemberStatus
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      return { success: false, error: 'Session expirée, veuillez vous reconnecter' };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/update-member-status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ org_id: orgId, member_id: memberId, status }),
    });

    const responseData = await response.json().catch(() => ({}));

    if (!response.ok || responseData?.error) {
      const errorMessage = responseData?.error || 'Erreur lors de la mise à jour du statut';
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (err) {
    console.error('Error updating member status:', err);
    return { success: false, error: 'Erreur inattendue lors de la mise à jour' };
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// INVITE MEMBER (via Edge Function)
// Uses fetch directly to avoid SDK throwing on non-2xx responses
// ─────────────────────────────────────────────────────────────────────────────
export async function inviteMember(
  orgId: string,
  email: string,
  role: string
): Promise<{ success: boolean; error?: string }> {
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData.session?.access_token;

    if (!accessToken) {
      return { success: false, error: 'Session expirée, veuillez vous reconnecter' };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/invite-member`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'apikey': supabaseAnonKey,
      },
      body: JSON.stringify({ org_id: orgId, email, role }),
    });

    const responseData = await response.json().catch(() => ({}));

    // Handle business errors (success: false in body) OR HTTP errors
    if (!response.ok || responseData?.success === false) {
      const errorMessage = responseData?.error || 'Erreur lors de l\'invitation';
      return { success: false, error: errorMessage };
    }

    return { success: true };
  } catch (err) {
    console.error('Error inviting member:', err);
    return { success: false, error: 'Erreur inattendue lors de l\'invitation' };
  }
}
